// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import StatementPanelTemplate from '@front/templates/StatementPanelTemplate.html';
import PanelSideBarBaseNoDataPartial from '@front/templates/PanelSideBarBaseNoDataPartial.html';
import { Resources } from '@shared/localizations/Resources';
import { LinkedSystem } from '../misc/LinkedSystem';
import { StatementItem } from '../cache/StatementItem';
import { ColouringModes } from '../elements/QuickTable/QuickTableColumn';
import { PanelNames } from '../UtilsClasses/FactoryConstants';
import { ReportType } from '@shared/commons/cache/AllowedReport';
import { SessionSettings } from '@shared/commons/SessionSettings';
import { ApplicationPanelWithTable } from './ApplicationPanelWithTable';
import { WDSettingsUtils } from '../UtilsClasses/WDGeneralSettingsUtils';
import { WDSettings } from '../settings/WDGeneralSettings';
import { ReportManager } from '@shared/utils/Managers/ReportManager';
import { NewContextMenuItemType } from '@shared/utils/ContextMenuUtils';
export class StatementPanel extends ApplicationPanelWithTable {
    constructor() {
        super();
        this.selectedStartTime = null;
        this.selectedFinishTime = null;
        this.Name = 'StatementPanel';
        this.headerLocaleKey = 'panel.statement';
    }
    getType() {
        return PanelNames.StatementPanel;
    }
    oncomplete() {
        super.oncomplete();
        this.addRangeSelectPanel();
        this.localize();
        this.populateTableContextMenu();
        this.displayAmountInLots = WDSettingsUtils.displayAmountInLots();
        WDSettings.settingsChanged.Subscribe(this.settingsChanged, this);
        if (!isValidNumber(this.InitGroupByColumnIndex)) {
            this.UpdateGroupByState(StatementItem.POSITION_ID_COL_INDEX);
        }
    }
    addRangeSelectPanel() {
        const topPanel = this.Controls.statementPanelRangeSelect;
        topPanel.set({
            refreshBtnTT: 'panel.statement.searchBtn.tt'
        });
        topPanel.getReportEvent.Subscribe(this.getStatementReport, this);
        topPanel.on('refreshClick', this.refreshStatementReport.bind(this));
        topPanel.updateSelectedRange();
        this.rangeSelectPanel = topPanel;
    }
    dispose() {
        this.rangeSelectPanel.getReportEvent.UnSubscribe(this.getStatementReport, this);
        WDSettings.settingsChanged.UnSubscribe(this.settingsChanged, this);
        super.dispose();
    }
    refreshStatementReport() {
        this.getStatementReport(this.selectedStartTime, this.selectedFinishTime);
    }
    populateTableContextMenu() {
        var _a;
        const items = [];
        this.AddExportContextMenuItem(items);
        items.push({
            type: NewContextMenuItemType.Default,
            text: 'screen.reports.print',
            event: super.printTable.bind(this),
            visible: !Resources.isHidden('panel.statement.print')
        });
        this.AddViewContextMenuItem(items);
        items.push({ type: NewContextMenuItemType.Separator });
        (_a = super.getQuickTable()) === null || _a === void 0 ? void 0 : _a.setTableContextMenuItems(items);
    }
    getStatementReport(startTime, finishTime) {
        this.selectedStartTime = startTime;
        this.selectedFinishTime = finishTime;
        void this.set('loading', true);
        ReportManager.reportsByComplited.set(ReportType.REPORT_STATEMENT, false);
        void ReportManager.sendRequestByDate(ReportType.REPORT_STATEMENT, startTime, finishTime)
            .then((statements) => {
            const qt = super.getQuickTable();
            if (qt == null)
                return;
            qt.ClearRows();
            const len = statements.length;
            for (let i = 0; i < len; i++) {
                qt.AddItem(new StatementItem(statements[i], SessionSettings));
            }
            void this.set({ isDataExist: len > 0 });
            if (LinkedSystem.accLinkingActive) {
                qt.filterByAccountNumber(LinkedSystem.getAccount());
            }
        })
            .finally(() => {
            void this.set('loading', false);
        });
    }
    settingsChanged() {
        const newSettingsInLots = WDSettingsUtils.displayAmountInLots();
        if (this.displayAmountInLots === newSettingsInLots) {
            return;
        }
        this.displayAmountInLots = newSettingsInLots;
        this.refreshStatementReport();
    }
    SetColumnsColouringMode(table) {
        super.ProcessSetColumnsColouringMode(table, [5], ColouringModes.Signed);
    }
    localize() {
        super.localize();
        this.set({
            noDataText: Resources.getResource('panel.statement.noData')
        });
    }
}
ApplicationPanelWithTable.extendWith(StatementPanel, {
    partials: {
        bodyPartial: StatementPanelTemplate,
        noDataPart: PanelSideBarBaseNoDataPartial
    },
    data: () => {
        return {
            isAccountLinkShow: true,
            isSymbolLinkShow: false,
            isDataExist: true,
            noDataText: '',
            noDataImg: 'statement'
        };
    }
});
